import { useCallback, useState } from 'react';
import { useSelector } from 'react-redux';

import { i18n } from '@lingui/core';
import { Trans, msg, t } from '@lingui/macro';
import PropTypes from 'prop-types';
import { Grid } from 'semantic-ui-react';

import { actionTypes as viewActionTypes } from 'actions/view';
import { useConceptLabelFormatter } from 'reducers/entityLabelFormatter';
import { createLoadingSelector } from 'reducers/ui';
import { eldersConceptSelector } from 'selectors/entities';
import {
  aggregatesSelector,
  getConceptAggregatesSelector,
  viewFacetSelector,
} from 'selectors/view';

import { SentimentChart } from 'components/customer/visualization/OverviewTimeSeriesVisualizations';
import EmptyDataPage from 'components/ui/EmptyDataPage';
import { NoMarginGrid } from 'components/ui/Grid';
import Header from 'components/ui/Header';
import ExportAsImage from 'components/ui/button/export-as/ExportAsImage';
import emptyExplorationUrl from 'components/ui/svg/undraw_visual_data_b1wx.svg';
import LoadingDataVisualization from 'components/ui/visualization/LoadingDataVisualization';
import Statistics from 'components/ui/visualization/Statistics';

import * as svars from 'assets/style/variables';

import {
  DISTRIBUTION_STATIC_DIMENSIONS,
  ExplorationBreakdownButtonWithModal,
} from './ExplorationBreakdownModal';

const CONCEPT_FOCUS_EXPORT_ID = 'explore-concept-focus';

const conceptAggregatesLoadingSelector = createLoadingSelector([
  viewActionTypes.FETCH_CONCEPT_AGGREGATES_REQUEST,
]);

function ExplorationConceptFocus({
  viewFacetId,
  conceptId,
  tagLabelFormatter,
  entityLabelFormatter,
  ontologyId,
  onSelectConcept,
}) {
  const [selectedConceptDistribution, setSelectedConceptDistribution] =
    useState(null);
  const closeConceptDistributionModal = useCallback(
    () => setSelectedConceptDistribution(null),
    []
  );
  const viewFacet = useSelector(viewFacetSelector);
  const conceptLabelFormatter = useConceptLabelFormatter();
  const eldersConcepts = useSelector(eldersConceptSelector)(conceptId);
  const { conceptTimeSeries, productKpis, sourceKpis, tagKpis } = useSelector(
    getConceptAggregatesSelector
  )(viewFacetId, conceptId);
  const { conceptKpis, kpis } = useSelector(aggregatesSelector);
  const conceptAggregatesIsLoading = useSelector(
    conceptAggregatesLoadingSelector
  );

  if (!conceptId) {
    return (
      <EmptyDataPage
        i18nHeaderText={msg({ id: 'dashboard-explore.explore-a-category' })}
        actionComponent={
          <Trans id="dashboard-explore.explore-a-category-description" />
        }
        illustrationUrl={emptyExplorationUrl}
      />
    );
  }
  let nChunks = 0;
  let averageSentiment = 0;
  let averageScore = 0;
  const selectedConceptKpis = conceptKpis?.[ontologyId]?.[conceptId];
  if (selectedConceptKpis) {
    nChunks = selectedConceptKpis[0].n_chunks;
    averageSentiment = selectedConceptKpis[0].average_sentiment;
    averageScore = selectedConceptKpis[0].average_score;
  }
  let breakdownModalProps = {
    globalAverageSentiment:
      (selectedConceptDistribution && kpis?.kpis?.[0]?.average_sentiment) || 0,
  };
  const staticDistributionItem = DISTRIBUTION_STATIC_DIMENSIONS.find(
    ({ value }) => value === selectedConceptDistribution
  );
  if (staticDistributionItem) {
    breakdownModalProps = {
      categoryHeader: i18n._(staticDistributionItem.text),
      categoryDataKey: selectedConceptDistribution,
      modalIsOpen: !!selectedConceptDistribution,
      labelFormatter: (id, metadata) =>
        entityLabelFormatter(staticDistributionItem.value, id, metadata),
    };
    if (staticDistributionItem.value === 'product_hierarchy') {
      const conceptProductKpis = productKpis;
      breakdownModalProps.disabled = !(conceptProductKpis?.length || false);
      breakdownModalProps.data = conceptProductKpis;
    } else {
      const conceptSourceKpis = sourceKpis;
      breakdownModalProps.disabled = !(conceptSourceKpis?.length || false);
      breakdownModalProps.data = conceptSourceKpis;
    }
  } else if (selectedConceptDistribution) {
    const conceptTagKpis = tagKpis?.kpis || {};
    breakdownModalProps = {
      categoryDataKey: 'tag',
      categoryHeader: selectedConceptDistribution
        ? entityLabelFormatter('tagSet', selectedConceptDistribution)
        : null,
      data: conceptTagKpis[selectedConceptDistribution] || null,
      disabled: !Object.keys(conceptTagKpis).length,
      modalIsOpen: selectedConceptDistribution,
      labelFormatter: tagLabelFormatter,
    };
  }
  const conceptName = conceptLabelFormatter(conceptId);
  return (
    <NoMarginGrid id={`${CONCEPT_FOCUS_EXPORT_ID}-${conceptId}`} stackable>
      <Grid.Row>
        <Grid.Column
          width={15}
          as={Header}
          data-testid="exploration-concept-name"
        >
          {conceptName}
        </Grid.Column>
        <Grid.Column width={1}>
          <ExportAsImage
            tooltipPosition="top left"
            disabled={conceptAggregatesIsLoading}
            exportId={`${CONCEPT_FOCUS_EXPORT_ID}-${conceptId}`}
            exportName={conceptName}
          />
        </Grid.Column>
      </Grid.Row>
      <Grid.Row centered>
        <Statistics
          nChunks={nChunks}
          averageSentiment={averageSentiment}
          averageScore={averageScore}
          shareOfExtracts={selectedConceptKpis?.[0]?.share_of_extracts}
          data-testid="exploration-sentiment-value"
        />
        <ExplorationBreakdownButtonWithModal
          title={t`dashboard-explore.concept-distribution-modal-title ${conceptName}`}
          onSelectConcept={onSelectConcept}
          onDistributionChange={(event, { value }) => {
            setSelectedConceptDistribution(value);
          }}
          currentDistribution={selectedConceptDistribution}
          modalIsOpen={!!selectedConceptDistribution}
          globalAverageSentiment={averageSentiment}
          onClose={closeConceptDistributionModal}
          path={eldersConcepts}
          withShareOfExtracts
          withReviewScore={viewFacet.use_review_score}
          style={{ padding: `${svars.spaceNormal} ${svars.spaceMedium}` }}
          {...breakdownModalProps}
        />
      </Grid.Row>
      <Grid.Row>
        <Grid.Column width={16}>
          {(conceptAggregatesIsLoading && (
            <LoadingDataVisualization height={svars.chartHeight} />
          )) ||
            (conceptTimeSeries && (
              <SentimentChart
                loading={conceptAggregatesIsLoading}
                data={conceptTimeSeries}
                useReviewScore={viewFacet.use_review_score}
                gradientName={`colorSentiment-${conceptId}`}
              />
            )) ||
            null}
        </Grid.Column>
      </Grid.Row>
    </NoMarginGrid>
  );
}

ExplorationConceptFocus.propTypes = {
  viewFacetId: PropTypes.string,
  conceptId: PropTypes.string,
  tagLabelFormatter: PropTypes.func.isRequired,
  entityLabelFormatter: PropTypes.func.isRequired,
  ontologyId: PropTypes.string.isRequired,
  onSelectConcept: PropTypes.func.isRequired,
};

ExplorationConceptFocus.defaultProps = {
  conceptId: null,
  viewFacetId: null,
};

export default ExplorationConceptFocus;
